/**
 * Inline Careers block for the COMPANY builder block list (PRD §5.2/§6.6).
 *
 * Renders expanded in place of the collapsed block row: header (icon, title,
 * "{open} open · {total} total", enable toggle, "+ Add job"), one row per
 * non-archived job with its Draft/Live/Offline label, a publish toggle wired
 * to the shared lifecycle actions (§7.6 — taking a live job offline confirms
 * first), an edit pencil, a delete button (archive, confirmed), and the
 * "Collect CVs for future roles" row bound to the openroles block's
 * talentPoolEnabled config.
 *
 * Company mode only — the individual builder keeps the collapsed BlockRow.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CompanyJobSummary } from '@tabbio/types';
import { colors } from '@tabbio/tokens';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import {
  formatCompanyJobLifecycle,
  getCompanyJobEmploymentLabel,
  getCompanyJobLifecycle,
  getCompanyJobWorkModeLabel,
} from '../../lib/company-jobs';
import type { PublicProfileBlock } from '../../lib/public-profile-builder';
import { PUBLIC_PROFILE_BLOCK_META } from '../../lib/public-profile-builder';
import type { JobLifecycleAction } from '../../hooks/useJobLifecycleActions';
import { useJobLifecycleActions } from '../../hooks/useJobLifecycleActions';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { isTalentPoolEnabled, withTalentPoolEnabled } from './useCompanyOpenRoles';

/** §7.6 toggle semantics: on = live. Draft publishes, offline reopens, live goes offline (confirmed). */
function jobPublishToggleAction(status: string): JobLifecycleAction | null {
  const lifecycle = getCompanyJobLifecycle(status);
  if (lifecycle === 'draft') return 'publish';
  if (lifecycle === 'live') return 'offline';
  if (lifecycle === 'offline') return 'reopen';
  return null;
}

const HAIRLINE = '1px solid var(--input-border)';

/**
 * Round 30px control used for the per-row edit/delete affordances. Hover is
 * what tells a mouse user these are buttons at all, and `danger` turns that
 * hover red so deleting reads as destructive before it is clicked.
 */
function RowIconButton({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  const [hovered, setHovered] = React.useState(false);
  const lit = hovered && !disabled;
  const tint = danger ? colors.danger : 'var(--text-primary)';

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onBlur={() => setHovered(false)}
      disabled={disabled}
      aria-label={label}
      title={label}
      style={{
        width: 30,
        height: 30,
        borderRadius: 15,
        border: lit && danger ? `1px solid ${colors.danger}` : HAIRLINE,
        // --hover is the theme-aware neutral (it flips to a white overlay in
        // dark mode); the danger tint is a wash of the same red the adjacent
        // "Offline" label uses, so the pair reads as one palette.
        backgroundColor: lit
          ? danger
            ? 'rgba(239,68,68,0.08)'
            : 'var(--hover)'
          : 'var(--bg)',
        display: 'grid',
        placeItems: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
    >
      <MaterialSymbol
        name={icon}
        size={14}
        style={{ color: lit ? tint : 'var(--text-secondary)' }}
      />
    </button>
  );
}

function InlineToggle({
  checked,
  onToggle,
  isRTL,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  onToggle: () => void;
  isRTL: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      onClick={onToggle}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative',
        padding: 0,
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        backgroundColor: checked ? '#22C55E' : 'rgba(0,0,0,0.06)',
        transition: 'background-color 150ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          insetInlineStart: 2,
          width: 22,
          height: 22,
          borderRadius: 11,
          backgroundColor: '#FFFFFF',
          transform: checked
            ? `translateX(${isRTL ? -18 : 18}px)`
            : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.2)',
        }}
      />
    </button>
  );
}

export function CareersBlockInline({
  block,
  jobs,
  isLoading,
  isRTL,
  isDragging,
  isDragOver,
  onOpenEditor,
  onToggleBlock,
  onAddJob,
  onEditJob,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDragEnter,
}: {
  block: PublicProfileBlock;
  jobs: CompanyJobSummary[];
  isLoading: boolean;
  isRTL: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onOpenEditor: () => void;
  onToggleBlock: () => void;
  onAddJob: () => void;
  onEditJob: (jobId: string) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnter: () => void;
}) {
  const { t } = useTranslation('common');
  const { t: tEmployer } = useTranslation('employer');
  const { setBlocks } = usePublicProfileBuilderModel();
  const lifecycleActions = useJobLifecycleActions();
  const [hover, setHover] = React.useState(false);

  const meta = PUBLIC_PROFILE_BLOCK_META[block.type];
  // Archived jobs leave everyday management (§7.6); the API already excludes
  // them, this filter is the local guarantee.
  const visibleJobs = React.useMemo(
    () => jobs.filter((job) => job.status !== 'archived'),
    [jobs],
  );
  const openCount = React.useMemo(
    () => visibleJobs.filter((job) => job.status === 'active').length,
    [visibleJobs],
  );
  const subtitle = isLoading
    ? (t(meta.subtitleKey as never) as string)
    : [
        t('publicProfile.openRoles.statsOpen' as never, { count: openCount }),
        t('publicProfile.openRoles.statsTotal' as never, {
          count: visibleJobs.length,
        }),
      ].join(' · ');

  const talentPoolEnabled = isTalentPoolEnabled(block);
  // Same mutation ProfileOpenRolesSheet uses. The value has to land in the
  // block config too: setBlocks writes the SERIALIZED blocks back into the
  // query cache, and serialization keeps only `config.talentPoolEnabled`.
  const setTalentPoolEnabled = React.useCallback(
    (value: boolean) => {
      setBlocks((previous) =>
        previous.map((item) =>
          item.id === block.id ? withTalentPoolEnabled(item, value) : item,
        ),
      );
    },
    [block.id, setBlocks],
  );

  return (
    <div
      data-profile-block-card
      data-block-id={block.id}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      style={{
        borderRadius: 20,
        backgroundColor: 'rgba(0,0,0,0.04)',
        outline: isDragOver ? '2px solid var(--text-secondary)' : 'none',
        outlineOffset: -2,
        opacity: isDragging ? 0.45 : isDragOver ? 0.95 : block.enabled ? 1 : 0.7,
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 14px',
        transition: 'opacity 120ms ease',
      }}
    >
      {/* Header row — same vocabulary as the collapsed BlockRow, plus Add job. */}
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <div
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onClick={(event) => event.stopPropagation()}
          aria-hidden
          style={{
            width: 22,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
            color: 'var(--text-tertiary)',
            opacity: hover ? 1 : 0.55,
            transition: 'opacity 140ms',
            flexShrink: 0,
          }}
        >
          <MaterialSymbol
            name="drag_indicator"
            size={20}
            style={{ color: 'var(--text-tertiary)' }}
          />
        </div>
        <button
          onClick={onOpenEditor}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            margin: 0,
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
            textAlign: 'start',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: 'rgba(0,0,0,0.06)',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--text-primary)',
              flexShrink: 0,
            }}
          >
            <MaterialSymbol name="work" size={18} />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: 'var(--text-primary)',
                lineHeight: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {block.customTitle || t(meta.titleKey as never)}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-tertiary)',
                lineHeight: '17px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </div>
          </div>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button
            onClick={onAddJob}
            style={{
              minHeight: 32,
              borderRadius: 999,
              border: 'none',
              backgroundColor: 'var(--text-primary)',
              color: 'var(--bg)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              paddingInline: 12,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <MaterialSymbol name="add" size={15} style={{ color: 'var(--bg)' }} />
            {t('publicProfile.openRoles.addJob')}
          </button>
          <InlineToggle
            checked={block.enabled}
            onToggle={onToggleBlock}
            isRTL={isRTL}
            ariaLabel={t(meta.titleKey as never) as string}
          />
        </div>
      </div>

      {/* Job rows — every non-archived job (§6.6). */}
      {!isLoading && visibleJobs.length === 0 ? (
        <div
          style={{
            borderTop: HAIRLINE,
            marginTop: 10,
            paddingTop: 12,
            paddingBottom: 4,
            paddingInlineStart: 34,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
            {t('publicProfile.openRoles.noJobs')}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)', lineHeight: '17px' }}>
            {t('publicProfile.openRoles.noJobsHint')}
          </span>
        </div>
      ) : null}
      {visibleJobs.map((job, index) => {
        const lifecycle = getCompanyJobLifecycle(job.status);
        const statusColor =
          lifecycle === 'live'
            ? colors.primary
            : lifecycle === 'draft'
              ? 'var(--text-tertiary)'
              : colors.danger;
        const toggleAction = jobPublishToggleAction(job.status);
        const isPending = lifecycleActions.isPending(job.id);
        const metaLine = [
          job.city || job.location,
          getCompanyJobWorkModeLabel(t, job.workMode),
          getCompanyJobEmploymentLabel(t, job.type),
        ]
          .filter(Boolean)
          .join(' · ');

        return (
          <div
            key={job.id}
            style={{
              borderTop: HAIRLINE,
              marginTop: index === 0 ? 10 : 0,
              paddingBlock: 10,
              paddingInlineStart: 34,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span
                dir="auto"
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  lineHeight: '20px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {job.title}
              </span>
              <span
                dir="auto"
                style={{
                  fontSize: 13,
                  color: 'var(--text-tertiary)',
                  lineHeight: '17px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {metaLine}
              </span>
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: statusColor,
                flexShrink: 0,
              }}
            >
              {formatCompanyJobLifecycle(t, job.status)}
            </span>
            <InlineToggle
              checked={lifecycle === 'live'}
              onToggle={() => {
                if (!toggleAction || isPending) return;
                void lifecycleActions.run(toggleAction, job.id);
              }}
              isRTL={isRTL}
              disabled={isPending || !toggleAction}
              ariaLabel={job.title}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <RowIconButton
                icon="edit"
                label={tEmployer('jobFlow.lifecycle.edit') as string}
                onClick={() => onEditJob(job.id)}
                disabled={isPending}
              />
              <RowIconButton
                icon="delete"
                label={tEmployer('jobFlow.lifecycle.delete') as string}
                danger
                onClick={() => {
                  if (isPending) return;
                  void lifecycleActions.run('delete', job.id, {
                    status: job.status,
                  });
                }}
                disabled={isPending}
              />
            </div>
          </div>
        );
      })}

      {/* Future roles — §6.6: own toggle, creates a talent-network submission. */}
      <div
        style={{
          borderTop: HAIRLINE,
          marginTop: isLoading ? 10 : 0,
          paddingTop: 12,
          paddingBottom: 2,
          paddingInlineStart: 34,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: 'rgba(0,0,0,0.06)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-secondary)',
            flexShrink: 0,
          }}
        >
          <MaterialSymbol name="groups" size={16} />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', lineHeight: '19px' }}>
            {t('publicProfile.openRoles.futureRoles.title')}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)', lineHeight: '17px' }}>
            {t('publicProfile.openRoles.futureRoles.helper')}
          </span>
        </div>
        <InlineToggle
          checked={talentPoolEnabled}
          onToggle={() => setTalentPoolEnabled(!talentPoolEnabled)}
          isRTL={isRTL}
          ariaLabel={t('publicProfile.openRoles.futureRoles.title') as string}
        />
        <RowIconButton
          icon="edit"
          label={t('publicProfile.openRoles.title') as string}
          onClick={onOpenEditor}
        />
      </div>
    </div>
  );
}
